import { ApolloClient, FP, GraphQL } from "./deps"

// Note also includes `data` and `errorInfo` fields, see `$util.error` in: https://docs.aws.amazon.com/appsync/latest/devguide/resolver-util-reference.html#utility-helpers-in-util
export type AWSAppSyncGraphqlError = GraphQL.GraphQLError & {
  errorType: string | null
}

function isAWSAppSyncGraphqlError(
  input: GraphQL.GraphQLError
): input is AWSAppSyncGraphqlError {
  return (
    typeof (input as AWSAppSyncGraphqlError).errorType === "string" ||
    (input as AWSAppSyncGraphqlError).errorType === null
  )
}

export type SellalongErrorType =
  | "sellalong-api-unexpected"
  | "sellalong-api-customer-not-found"

export function isSellalongClientError(
  error: Error
): error is SellalongClientError {
  return error instanceof SellalongClientError
}

export function isCustomerNotFoundError(error: Error) {
  return (
    isSellalongClientError(error) &&
    error.type === "sellalong-api-customer-not-found"
  )
}

export class SellalongClientError extends Error {
  constructor(
    public type: SellalongErrorType,
    message?: string,
    public cause?: Error
  ) {
    super(`${type}: ${message || cause?.message}`)
    this.name = "SellalongClientError"
  }

  static from(type: SellalongErrorType, cause: unknown): SellalongClientError {
    if (cause instanceof Error) {
      if (ApolloClient.isApolloError(cause)) {
        return SellalongClientError.fromApolloError(type, cause)
      }
      return new SellalongClientError(type, undefined, cause)
    }

    return new SellalongClientError(type, `non-error: ${JSON.stringify(cause)}`)
  }

  static fromApolloError(
    type: SellalongErrorType,
    cause: ApolloClient.ApolloError
  ): SellalongClientError {
    return FP.function.pipe(
      cause.graphQLErrors,
      FP.readonlyNonEmptyArray.fromReadonlyArray,
      FP.option.filter((errors) => errors.length === 1),
      FP.option.map(FP.readonlyNonEmptyArray.head),
      FP.option.filter(isAWSAppSyncGraphqlError),
      FP.option.filter(
        (error) =>
          error.errorType === "CUSTOMER_EMAIL_NOT_FOUND" ||
          error.errorType === "CUSTOMER_NOT_FOUND"
      ),
      FP.option.fold(
        () => new SellalongClientError(type, undefined, cause),
        (_error) =>
          new SellalongClientError(
            "sellalong-api-customer-not-found",
            undefined,
            cause
          )
      )
    )
  }
}
